/**
 * Procedural "Slime Buddy" model, copied verbatim from the `img2threejs`
 * sculpting tool (`C:\Gantz-System\img2threejs\src\createObjectModel.ts`,
 * export `createSlimeBuddyModel`) and integrated here as a dev preview.
 *
 * Fully procedural: every mesh is built from Three.js primitives at
 * construction time, no textures or imported models are loaded. The source
 * reference image and generation spec that guided this sculpt are kept out
 * of the shipped bundle, under `provenance/slime-buddy/` at the repo root -
 * see `PROVENANCE.md`.
 *
 * Not wired into gameplay: no room or Chimera kind in this project is a
 * slime. This is exposed as an opt-in dev preview (`?slime=1`, see
 * `Renderer.showSlimeBuddyPreview`), the same pattern already used for the
 * Warden test content - additive, walled off, no Round 1 room-budget impact.
 */
import * as THREE from 'three';

export type SlimeOptions = { outline?: boolean };

const palette = { lime: 0xace600, yellowLime: 0xd8fa08, shadowLime: 0x72b900, ink: 0x203908, highlight: 0xf7ffd8 };

function physical(color: number, roughness = 0.24) {
  return new THREE.MeshPhysicalMaterial({ color, metalness: 0, roughness, clearcoat: 0.82, clearcoatRoughness: 0.16, ior: 1.38, reflectivity: 0.48 });
}

const slimeMaterial = physical(palette.lime, 0.2); slimeMaterial.vertexColors = true;
const slimeSolid = physical(0xbfea05, 0.21);
const lipMaterial = physical(0x9bd900, 0.26);
const irisMaterial = physical(palette.yellowLime, 0.18);
const shadowMaterial = physical(palette.shadowLime, 0.34);
const inkMaterial = new THREE.MeshStandardMaterial({ color: palette.ink, roughness: 0.52 });
const outlineMaterial = new THREE.MeshBasicMaterial({ color: palette.ink, side: THREE.BackSide });
const highlightMaterial = new THREE.MeshBasicMaterial({ color: palette.highlight, toneMapped: false });

function addVertexGradient(geometry: THREE.BufferGeometry) {
  const position = geometry.getAttribute('position');
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const low = new THREE.Color(0x83c900), middle = new THREE.Color(0xa8e000), high = new THREE.Color(0xd5f807);
  const color = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i += 1) {
    const t = THREE.MathUtils.clamp((position.getY(i) - box.min.y) / (box.max.y - box.min.y), 0, 1);
    const c = t < 0.5 ? low.clone().lerp(middle, t * 2) : middle.clone().lerp(high, (t - 0.5) * 2);
    c.offsetHSL(0, 0, 0.025 * Math.sin(position.getX(i) * 9 + position.getZ(i) * 7));
    color.set([c.r, c.g, c.b], i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(color, 3));
}

function outlinedMesh(name: string, geometry: THREE.BufferGeometry, material: THREE.Material, outlineScale = 1.035) {
  const group = new THREE.Group(); group.name = name;
  const mesh = new THREE.Mesh(geometry, material); mesh.name = `${name}-surface`; mesh.castShadow = true; mesh.receiveShadow = true; group.add(mesh);
  const outline = new THREE.Mesh(geometry, outlineMaterial); outline.name = `${name}-outline`; outline.scale.setScalar(outlineScale); outline.renderOrder = -1; group.add(outline);
  return group;
}

function createBody() {
  const profile = [
    new THREE.Vector2(0.48, -0.73), new THREE.Vector2(0.78, -0.68), new THREE.Vector2(0.96, -0.51),
    new THREE.Vector2(1.02, -0.20), new THREE.Vector2(0.97, 0.12), new THREE.Vector2(0.81, 0.37),
    new THREE.Vector2(0.58, 0.52), new THREE.Vector2(0.31, 0.59), new THREE.Vector2(0.08, 0.61),
  ];
  const smoothProfile = new THREE.SplineCurve(profile).getPoints(36);
  const geometry = new THREE.LatheGeometry(smoothProfile, 96); geometry.computeVertexNormals(); addVertexGradient(geometry);
  const body = outlinedMesh('body', geometry, slimeMaterial, 1.026); body.scale.z = 0.9; return body;
}

function createCrest() {
  const points = [new THREE.Vector2(0.34,0),new THREE.Vector2(0.31,0.08),new THREE.Vector2(0.24,0.16),new THREE.Vector2(0.16,0.27),new THREE.Vector2(0.10,0.39),new THREE.Vector2(0.045,0.51),new THREE.Vector2(0,0.59)];
  const crest = outlinedMesh('crown-crest', new THREE.LatheGeometry(points, 64), slimeSolid, 1.055);
  crest.position.set(-0.035, 0.53, 0); crest.rotation.z = -0.08; crest.scale.set(0.92,0.78,0.92); return crest;
}

function makeEye(characterSide: 'left' | 'right') {
  const sign = characterSide === 'left' ? 1 : -1;
  const eye = new THREE.Group(); eye.name = `eye-${characterSide}`; eye.position.set(sign * 0.80, 0.20, 0.60); eye.rotation.y = sign * 0.10;
  const socket = outlinedMesh(`eye-${characterSide}-socket`, new THREE.SphereGeometry(1, 40, 24), slimeSolid, 1.06); socket.scale.set(0.205, 0.205, 0.145); eye.add(socket);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.118, 0.026, 14, 48), shadowMaterial); rim.name = `eye-${characterSide}-outer-ring`; rim.position.z = 0.140; eye.add(rim);
  const iris = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 20), irisMaterial); iris.name = `eye-${characterSide}-iris`; iris.scale.set(0.086, 0.086, 0.038); iris.position.z = 0.158; eye.add(iris);
  const inner = new THREE.Mesh(new THREE.TorusGeometry(0.059, 0.013, 10, 36), inkMaterial); inner.name = `eye-${characterSide}-inner-ring`; inner.position.z = 0.192; eye.add(inner);
  const glint = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 10), highlightMaterial); glint.name = `eye-${characterSide}-glint`; glint.scale.set(0.021, 0.037, 0.012); glint.position.set(-0.030, 0.036, 0.201); glint.rotation.z = -0.5; eye.add(glint);
  return eye;
}

function tube(points: THREE.Vector3[], radius: number, material: THREE.Material, name: string) {
  const mesh = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.42), 64, radius, 14, false), material);
  mesh.name = name; mesh.castShadow = true; return mesh;
}

function createMouth() {
  const mouth = new THREE.Group(); mouth.name = 'mouth';
  const upper = [new THREE.Vector3(-0.72,-0.05,0.575),new THREE.Vector3(-0.51,0,0.705),new THREE.Vector3(-0.25,0.16,0.815),new THREE.Vector3(0,0.20,0.845),new THREE.Vector3(0.25,0.16,0.815),new THREE.Vector3(0.51,0,0.705),new THREE.Vector3(0.72,-0.05,0.575)];
  const lower = [new THREE.Vector3(-0.72,-0.05,0.575),new THREE.Vector3(-0.50,-0.10,0.710),new THREE.Vector3(-0.26,-0.18,0.805),new THREE.Vector3(0,-0.21,0.835),new THREE.Vector3(0.26,-0.18,0.805),new THREE.Vector3(0.50,-0.10,0.710),new THREE.Vector3(0.72,-0.05,0.575)];
  const cavity = new THREE.Mesh(new THREE.SphereGeometry(1,40,20),inkMaterial); cavity.name='mouth-cavity'; cavity.scale.set(.50,.12,.045); cavity.position.set(0,-.035,.79); mouth.add(cavity);
  mouth.add(tube(upper, 0.09, inkMaterial, 'upper-lip-outline'), tube(lower, 0.085, inkMaterial, 'lower-lip-outline'));
  mouth.add(tube(upper.map(p=>p.clone().add(new THREE.Vector3(0,0.012,0.055))),0.058,shadowMaterial,'upper-lip'));
  mouth.add(tube(lower.map(p=>p.clone().add(new THREE.Vector3(0,-0.006,0.055))),0.054,lipMaterial,'lower-lip'));
  const innerLip = new THREE.Mesh(new THREE.SphereGeometry(1,40,20),irisMaterial); innerLip.name='lower-lip-cushion'; innerLip.scale.set(0.34,0.14,0.07); innerLip.position.set(0,-0.11,0.865); mouth.add(innerLip);
  const glint = new THREE.Mesh(new THREE.SphereGeometry(1,18,10),highlightMaterial); glint.name='lip-glint'; glint.scale.set(0.055,0.022,0.012); glint.position.set(0.08,-0.11,0.918); mouth.add(glint);
  return mouth;
}

function addBodyHighlights(body: THREE.Group) {
  const specs: Array<[string,number,number,number,number,number]> = [
    ['highlight-upper-left',-0.48,0.26,0.755,0.052,0.13],['highlight-upper-right',0.43,0.35,0.745,0.075,0.15],
    ['highlight-lower-left',-0.70,-0.43,0.57,0.043,0.075],['highlight-lower-right',0.55,-0.50,0.62,0.05,0.025],
  ];
  for (const [name,x,y,z,sx,sy] of specs) { const spot=new THREE.Mesh(new THREE.SphereGeometry(1,18,10),highlightMaterial); spot.name=name; spot.scale.set(sx,sy,0.018); spot.position.set(x,y,z); spot.rotation.z=x<0?-0.35:0.55; body.add(spot); }
  const crown=new THREE.Mesh(new THREE.SphereGeometry(1,18,10),highlightMaterial); crown.name='crest-glint'; crown.scale.set(0.038,0.17,0.018); crown.position.set(-0.085,0.88,0.19); crown.rotation.z=-0.12; body.add(crown);
}

export function createSlimeBuddyModel(options: SlimeOptions = {}) {
  const root=new THREE.Group(); root.name='slime-buddy-root';
  const body=createBody(); root.add(body);
  const crest=createCrest(), eyeLeft=makeEye('left'), eyeRight=makeEye('right'), mouth=createMouth(); mouth.position.set(0,-.03,.12); mouth.scale.set(1.02,1,1); body.add(crest,eyeLeft,eyeRight,mouth); addBodyHighlights(body);
  if(options.outline===false) root.traverse(node=>{if(node.name.endsWith('-outline')) node.visible=false;});
  const nodes={body,crest,eyeLeft,eyeRight,mouth};
  const sockets={crown:new THREE.Object3D(),eyeLeft:new THREE.Object3D(),eyeRight:new THREE.Object3D(),face:new THREE.Object3D()};
  sockets.crown.position.set(0,0.54,0); sockets.eyeLeft.position.copy(eyeLeft.position); sockets.eyeRight.position.copy(eyeRight.position); sockets.face.position.set(0,-0.05,0.72);
  Object.entries(sockets).forEach(([name,socket])=>{socket.name=`socket-${name}`;body.add(socket);});
  const meshes:Record<string,THREE.Mesh>={}; root.traverse(node=>{const mesh=node as THREE.Mesh; if(mesh.isMesh)meshes[node.name]=mesh;});
  const rest={crest:crest.position.clone(),eyeLeft:eyeLeft.position.clone(),eyeRight:eyeRight.position.clone(),mouth:mouth.position.clone()};
  const setExplode=(amount:number)=>{crest.position.copy(rest.crest).addScaledVector(new THREE.Vector3(0,1,0),amount*.45);eyeLeft.position.copy(rest.eyeLeft).addScaledVector(new THREE.Vector3(1,.15,.25),amount*.45);eyeRight.position.copy(rest.eyeRight).addScaledVector(new THREE.Vector3(-1,.15,.25),amount*.45);mouth.position.copy(rest.mouth).addScaledVector(new THREE.Vector3(0,-.2,1),amount*.35);};
  root.userData.sculptRuntime={profile:'generic-animated-prop',nodes,meshes,sockets,colliders:{body:{type:'ellipsoid',scale:[1.02,.74,.92]}},destructionGroups:{},setExplode};
  return root;
}
